import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, MapPin, Users } from 'lucide-react';
interface FilterModalProps {
  onClose: () => void;
}
export function FilterModal({
  onClose
}: FilterModalProps) {
  const [distance, setDistance] = useState(25);
  const [ageRange, setAgeRange] = useState({
    min: 21,
    max: 35
  });
  const [showMe, setShowMe] = useState<'women' | 'men' | 'everyone'>('women');
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="absolute inset-0 z-50 bg-black/50 backdrop-blur-sm" onClick={onClose} data-id="element-187">
      <motion.div initial={{
      y: '100%'
    }} animate={{
      y: 0
    }} exit={{
      y: '100%'
    }} transition={{
      type: 'spring',
      damping: 25,
      stiffness: 200
    }} className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl p-6 max-h-[85vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-id="element-188">
        {/* Header */}
        <div className="flex items-center justify-between mb-6" data-id="element-189">
          <h2 className="text-xl font-bold text-zinc-900" data-id="element-190">
            Discovery Settings
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-zinc-100 rounded-full transition-colors" data-id="element-191">
            <X size={24} className="text-zinc-400" data-id="element-192" />
          </button>
        </div>

        {/* Show Me */}
        <div className="mb-8" data-id="element-193">
          <div className="flex items-center gap-2 mb-4" data-id="element-194">
            <Users size={20} className="text-zinc-400" data-id="element-195" />
            <span className="font-semibold text-zinc-900" data-id="element-196">Show Me</span>
          </div>
          <div className="flex gap-2" data-id="element-197">
            {(['women', 'men', 'everyone'] as const).map(option => <button key={option} onClick={() => setShowMe(option)} className={`flex-1 py-3 rounded-xl font-medium capitalize transition-all ${showMe === option ? 'bg-gradient-to-r from-orange-500 to-rose-500 text-white shadow-lg' : 'bg-zinc-100 text-zinc-600 hover:bg-zinc-200'}`} data-id="element-198">
                {option}
              </button>)}
          </div>
        </div>

        {/* Distance */}
        <div className="mb-8" data-id="element-199">
          <div className="flex items-center justify-between mb-4" data-id="element-200">
            <div className="flex items-center gap-2" data-id="element-201">
              <MapPin size={20} className="text-zinc-400" data-id="element-202" />
              <span className="font-semibold text-zinc-900" data-id="element-203">
                Maximum Distance
              </span>
            </div>
            <span className="text-rose-500 font-bold" data-id="element-204">{distance} mi</span>
          </div>
          <input type="range" min="1" max="100" value={distance} onChange={e => setDistance(Number(e.target.value))} className="w-full h-2 bg-zinc-200 rounded-full appearance-none cursor-pointer accent-rose-500" data-id="element-205" />
          <div className="flex justify-between text-xs text-zinc-400 mt-2" data-id="element-206">
            <span data-id="element-207">1 mi</span>
            <span data-id="element-208">100 mi</span>
          </div>
        </div>

        {/* Age Range */}
        <div className="mb-8" data-id="element-209">
          <div className="flex items-center justify-between mb-4" data-id="element-210">
            <span className="font-semibold text-zinc-900" data-id="element-211">Age Range</span>
            <span className="text-rose-500 font-bold" data-id="element-212">
              {ageRange.min} - {ageRange.max}
            </span>
          </div>
          <div className="space-y-4" data-id="element-213">
            <div data-id="element-214">
              <label className="text-xs text-zinc-400 mb-1 block" data-id="element-215">
                Minimum Age
              </label>
              <input type="range" min="18" max="50" value={ageRange.min} onChange={e => setAgeRange({
              ...ageRange,
              min: Math.min(Number(e.target.value), ageRange.max - 1)
            })} className="w-full h-2 bg-zinc-200 rounded-full appearance-none cursor-pointer accent-rose-500" data-id="element-216" />
            </div>
            <div data-id="element-217">
              <label className="text-xs text-zinc-400 mb-1 block" data-id="element-218">
                Maximum Age
              </label>
              <input type="range" min="18" max="60" value={ageRange.max} onChange={e => setAgeRange({
              ...ageRange,
              max: Math.max(Number(e.target.value), ageRange.min + 1)
            })} className="w-full h-2 bg-zinc-200 rounded-full appearance-none cursor-pointer accent-rose-500" data-id="element-219" />
            </div>
          </div>
        </div>

        {/* Global Toggle */}
        <div className="flex items-center justify-between py-4 border-t border-zinc-100" data-id="element-220">
          <div data-id="element-221">
            <p className="font-semibold text-zinc-900" data-id="element-222">Global Mode</p>
            <p className="text-sm text-zinc-500" data-id="element-223">
              See people from around the world
            </p>
          </div>
          <button className="w-12 h-7 bg-zinc-200 rounded-full relative transition-colors" data-id="element-224">
            <div className="absolute left-1 top-1 w-5 h-5 bg-white rounded-full shadow-sm transition-transform" data-id="element-225" />
          </button>
        </div>

        {/* Apply Button */}
        <button onClick={onClose} className="w-full py-4 bg-gradient-to-r from-orange-500 to-rose-500 text-white rounded-2xl font-bold text-lg mt-6 shadow-lg shadow-rose-500/30 hover:shadow-xl hover:shadow-rose-500/40 transition-shadow" data-id="element-226">
          Apply Filters
        </button>
      </motion.div>
    </motion.div>;
}